import { FC, useEffect, useRef, useState } from 'react';
import url from '@/shared/lib/url';
import { useLoginStore } from '@/shared/lib/store/model/login.store';
import common from '@/shared/lib/common';
import viewer from '@/shared/utils/viewer';

export const KvResultContainer: FC<KvResultContainerProps> = (props) => {
    const {
        errata,
        setId,
        articleId,
        subId,
        questionNumber,
        isHand = 'N',
        isMark,
        isPeerReview = 'N',
        reloadKey,
        overflowY,
        sendEvaluation,
        mode,
        selectorIdx,
        child,
        eventListener,
        onQuestionCount,
        width,
    } = props;
    const { loginInfo } = useLoginStore();
    const viewerContainerRef = useRef<HTMLDivElement>(null);
    const eventListenerRef = useRef(eventListener);
    const onQuestionCountRef = useRef(onQuestionCount);

    const apiHost = url.lCmsApi();
    const uploadApiHost = import.meta.env.VITE_AIDT_URL_LMSAPI_VIEWER;
    const fileHost = import.meta.env.VITE_URL_CMS;

    // const fileHost = import.meta.env.VITE_URL_TEST_CMS;
    // const uploadApiHost = import.meta.env.VITE_AIDT_URL_TEST_LMSAPI_VIEWER;
    // const apiHost = import.meta.env.VITE_AIDT_URL_TEST_LCMSAPI;

    const [viewerHtml, setViewerHtml] = useState<string>('');

    const uid = loginInfo?.memberId;
    const id = loginInfo?.memberId;
    const gubun = 'T';

    // const isMath = props.subject != 'engl';
    // const skin = isMath ? 'math' : 'english';
    const skin = 'math';

    const getOverflowY = () => {
        switch (overflowY) {
            case 'none':
                return '';
            case 'auto':
                return 'overflow-y: auto;';
            default:
                return 'overflow-y: scroll;';
        }
    };
    const styleOverflowY = getOverflowY();
    const questionNumberJson = questionNumber ? JSON.stringify({ data: questionNumber }) : '';
    const shouldReportQuestionCount = Boolean(onQuestionCount);

    useEffect(() => {
        eventListenerRef.current = eventListener;
        onQuestionCountRef.current = onQuestionCount;
    }, [eventListener, onQuestionCount]);

    useEffect(() => {
        const html = `<kv-result-container 
            service_type="sbank"
            errata="${errata}"
            ${reloadKey ? 'id="' + reloadKey + '"' : ''}
            style="height: 100%; width: 100%; --kve-sans-serif: sans-serif;--kve-serif:serif;${styleOverflowY}display: block;${isHand === 'Y' ? 'padding-top:15px;' : ''}"
            class="container"
            userDiv="${gubun}"
            ${setId ? 'set_id=' + setId : ''}
            article_id="${articleId}"
            ${subId ? 'sub_id=' + subId : ''}
            question_number='${questionNumberJson}'
            mode=${mode ? mode : 'slide'}
            skin="${skin}"
            is_hand="${isHand}"
            ${isMark ? 'is_mark=' + isMark : ''}
            is_preview="Y"
            is_peer_review="${isPeerReview}"
            user_info='{"uid":"${uid}","id":"${id}","name":"${uid}"}'
            api_host="${apiHost}" 
            upload_api_host="${uploadApiHost}"
            file_host="${fileHost}"
            ${sendEvaluation ? 'send_evaluation=' + sendEvaluation : ''}
        ></kv-result-container>`;
        setViewerHtml(html);
    }, [
        apiHost,
        articleId,
        errata,
        fileHost,
        id,
        isHand,
        isMark,
        isPeerReview,
        mode,
        questionNumberJson,
        reloadKey,
        sendEvaluation,
        setId,
        styleOverflowY,
        subId,
        uid,
        uploadApiHost,
    ]);

    useEffect(() => {
        let questionCountTimer: number | undefined;
        let questionCountCheckCount = 0;

        const clearQuestionCountTimer = () => {
            if (questionCountTimer !== undefined) {
                window.clearTimeout(questionCountTimer);
                questionCountTimer = undefined;
            }
        };

        const getViewerQuestionInfo = (root: ParentNode) => {
            let chainQuestionCount = 0;
            let hasQuestionNumber = false;

            root.querySelectorAll<HTMLElement>('*').forEach((element) => {
                if (element.matches('section.kve-article')) {
                    chainQuestionCount += 1;
                }
                if (element.classList.contains('q-number')) {
                    hasQuestionNumber = true;
                }
                if (element.shadowRoot) {
                    const nestedInfo = getViewerQuestionInfo(element.shadowRoot);
                    chainQuestionCount += nestedInfo.chainQuestionCount;
                    hasQuestionNumber ||= nestedInfo.hasQuestionNumber;
                }
            });

            return { chainQuestionCount, hasQuestionNumber };
        };

        const reportQuestionCount = (selector: Element) => {
            const questionCountListener = onQuestionCountRef.current;
            if (!questionCountListener) return;

            const { chainQuestionCount, hasQuestionNumber } = selector.shadowRoot
                ? getViewerQuestionInfo(selector.shadowRoot)
                : { chainQuestionCount: 0, hasQuestionNumber: false };

            if (!hasQuestionNumber && questionCountCheckCount < 60) {
                questionCountCheckCount += 1;
                clearQuestionCountTimer();
                questionCountTimer = window.setTimeout(() => reportQuestionCount(selector), 50);
                return;
            }

            clearQuestionCountTimer();
            questionCountListener(chainQuestionCount || 1);
        };

        const viewerEventListener = (e: any) => {
            common.log('[NAVI] 결과 뷰어 이벤트 수신', e);
            const selector = e.currentTarget;
            if (selector instanceof Element) {
                reportQuestionCount(selector);
            }
            if (eventListenerRef.current) {
                eventListenerRef.current(e);
            }
        };

        const linkToPageEventListenser = async (e: any) => {
            await viewer.linkToPageEventListenser(e);
        };

        const selector =
            child && selectorIdx && selectorIdx > 0
                ? document.getElementsByTagName('kv-result-container')[selectorIdx]
                : viewerContainerRef.current?.querySelector('kv-result-container');

        if (!selector) {
            return;
        }

        reportQuestionCount(selector);

        selector.addEventListener('textbook-handprinting-load', viewerEventListener); // (학생) 위치 이동(모으기)
        selector.addEventListener('loading-status', viewerEventListener);
        selector.addEventListener('evaluation-load', viewerEventListener);
        selector.addEventListener('link-to-page', linkToPageEventListenser);

        return () => {
            clearQuestionCountTimer();
            selector.removeEventListener('textbook-handprinting-load', viewerEventListener);
            selector.removeEventListener('loading-status', viewerEventListener);
            selector.removeEventListener('evaluation-load', viewerEventListener);
            selector.removeEventListener('link-to-page', linkToPageEventListenser);
        };
    }, [child, selectorIdx, shouldReportQuestionCount, viewerHtml]);

    return (
        <div
            ref={viewerContainerRef}
            style={width ? { width: width } : undefined}
            dangerouslySetInnerHTML={{ __html: viewerHtml }}
        />
    );
};

export type KvResultContainerMsgType =
    | 'notify-textbook-handprinting-load'
    | 'receive-user-report'
    | 'notify-evaluation-load-';

export const sendToKvReesultContainer = (
    _data: any,
    type: KvResultContainerMsgType,
    key?: any,
    index?: number
) => {
    const data = {
        userDiv: 'S',
        article_id: _data.article_id,
        sub_id: _data.sub_id,
        success: true,
        resultMessage: '손글씨 호출',
        resultCode: 200,
        paramData: {
            resultDetailId: _data.resultDetailId,
        },
        resultData: {
            resultDetailId: _data.resultDetailId,
            hdwrtCn: _data.hdwrtCn,
            resultOk: true,
            resultMsg: '성공',
        },
    };
    viewer.sendToElement('kv-result-container', data, type, key, index);
};

export const sendSlfperEvlToKvResultContainer = (
    _data: any,
    type: KvResultContainerMsgType,
    key?: any
) => {
    const data = {
        slfPerList: _data,
    };
    viewer.sendToElement('kv-result-container', data, type, key);
};

export const recevieUserReport = (
    data: any,
    type: KvResultContainerMsgType,
    key?: any,
    index?: number
) => {
    let strData = '';
    if (data.errata !== null && data.errata !== undefined) {
        strData = '{\"subMitAnw\" : ' + data.subMitAnw + ', \"errata\" : ' + data.errata + '}';
    } else {
        strData = '{\"subMitAnw\" : ' + data.subMitAnw + '}';
    }

    viewer.sendToElement('kv-result-container', strData, type, key, index);
};
